/**
 * sidebarWorkerRows.logic — pure helpers for worker sub-rows and attention
 * bubbles in the sidebar (M4.1 + M4.6).
 *
 * All functions are pure and referentially transparent for unit-test coverage.
 */

import type { ThreadKind, ThreadStatus, WorkerRole } from "@cmmd-forge/contracts";
import { WORKER_STATUS_REQUIRES_ATTENTION } from "../workbar/workbarAgents.logic";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal worker data needed by the sidebar for sub-row rendering. */
export interface WorkerSubRowItem {
  readonly id: string;
  readonly title: string;
  readonly status: ThreadStatus;
  readonly workerRole?: WorkerRole | null;
}

/** Worker data extended with the parent thread link used for filtering. */
export interface WorkerWithParent extends WorkerSubRowItem {
  readonly kind?: ThreadKind;
  readonly parentThreadId: string | null;
}

// ---------------------------------------------------------------------------
// computeMasterAttentionCount
// ---------------------------------------------------------------------------

/**
 * Count of workers with an attention-requiring status (stuck / needs-review /
 * conflicted). Used to drive the attention chip on a lead's sidebar row.
 */
export function computeMasterAttentionCount(workers: readonly WorkerSubRowItem[]): number {
  let count = 0;
  for (const w of workers) {
    if (WORKER_STATUS_REQUIRES_ATTENTION.has(w.status)) count += 1;
  }
  return count;
}

export function formatAgentCountLabel(count: number): string {
  return `${count} ${count === 1 ? "agent" : "agents"}`;
}

// ---------------------------------------------------------------------------
// computeWorkspaceAttentionCount
// ---------------------------------------------------------------------------

/**
 * Sum of per-lead attention counts across an entire workspace.
 * Used to drive the workspace-level attention chip.
 *
 * @param masterAttentions - array of per-master attention counts
 */
export function computeWorkspaceAttentionCount(masterAttentions: readonly number[]): number {
  let total = 0;
  for (const n of masterAttentions) total += n;
  return total;
}

// ---------------------------------------------------------------------------
// buildWorkerSubRows
// ---------------------------------------------------------------------------

/**
 * Filter an array of all-threads-in-project down to those that are workers
 * belonging to a specific master, then sort so attention-requiring workers
 * appear first.
 */
export function buildWorkerSubRows(
  allWorkers: readonly WorkerWithParent[],
  masterThreadId: string,
): readonly WorkerSubRowItem[] {
  const owned = allWorkers.filter(
    (w) => (w.kind ?? "worker") === "worker" && w.parentThreadId === masterThreadId,
  );

  return [...owned].toSorted((a, b) => {
    const aAttn = WORKER_STATUS_REQUIRES_ATTENTION.has(a.status) ? 1 : 0;
    const bAttn = WORKER_STATUS_REQUIRES_ATTENTION.has(b.status) ? 1 : 0;
    return bAttn - aAttn;
  });
}
